import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Code2, Key, Plus, Trash2, Edit2, Check, Copy, Globe } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

// ---- Types ----

interface ApiEndpoint {
  id: string;
  name: string;
  pathSuffix: string;
  method: 'GET' | 'POST';
  authType: 'API_KEY' | 'JWT' | 'OAUTH';
  queryTemplateJson: string;
  rateLimitPerMinute: number;
  enabled: boolean;
  createdAt: string;
}

interface ApiKey {
  id: string;
  name: string;
  enabled: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

interface QueryTemplate {
  table: string;
  operation: 'list' | 'count' | 'sum';
  filter: Record<string, string>;
  column?: string;
  limit?: number;
}

const ALLOWED_TABLES = ['leads', 'opportunities', 'accounts', 'contacts'];
const FILTER_COLUMNS: Record<string, string[]> = {
  leads: ['status', 'source', 'campaign_id', 'assigned_rep_id'],
  opportunities: ['status', 'current_stage_id', 'account_id', 'assigned_rep_id'],
  accounts: ['status', 'industry', 'size_bucket'],
  contacts: ['account_id', 'status', 'buying_role'],
};
const SUM_COLUMNS: Record<string, string[]> = {
  opportunities: ['estimated_deal_value'],
};

// ---- Main component ----

/**
 * Story 82.4 — /admin/api-builder
 *
 * Two tabs: Endpoints (CRUD + form with structured query template builder)
 * and API Keys (create + delete, raw key shown once).
 */
export function ApiBuilderPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'endpoints' | 'keys'>('endpoints');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('apiBuilder.endpoints.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('apiBuilder.endpoints.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setTab('endpoints')}
          className={cn(
            'px-4 py-2 text-sm font-medium transition-colors',
            tab === 'endpoints'
              ? 'border-b-2 border-primary text-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Globe className="mr-1.5 inline h-4 w-4" />
          {t('apiBuilder.endpoints.title')}
        </button>
        <button
          onClick={() => setTab('keys')}
          className={cn(
            'px-4 py-2 text-sm font-medium transition-colors',
            tab === 'keys'
              ? 'border-b-2 border-primary text-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Key className="mr-1.5 inline h-4 w-4" />
          {t('apiBuilder.keys.title')}
        </button>
      </div>

      {tab === 'endpoints' ? <EndpointsTab /> : <ApiKeysTab />}
    </div>
  );
}

// ---- Endpoints tab ----

function EndpointsTab() {
  const { t } = useTranslation();
  const [endpoints, setEndpoints] = useState<ApiEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ApiEndpoint | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get<ApiEndpoint[]>('/api-builder/endpoints')
      .then(({ data }) => setEndpoints(data))
      .catch(() => setEndpoints([]))
      .finally(() => setLoading(false));
  }, [refreshToken]);

  function handleEdit(ep: ApiEndpoint) {
    setEditing(ep);
    setFormOpen(true);
  }

  async function handleDelete(id: string) {
    if (!confirm(t('apiBuilder.endpoints.deleteConfirm'))) return;
    try {
      await apiClient.delete(`/api-builder/endpoints/${id}`);
      setRefreshToken((x) => x + 1);
    } catch {
      setError(t('apiBuilder.endpoints.error_delete'));
    }
  }

  function handleSaved() {
    setFormOpen(false);
    setEditing(null);
    setRefreshToken((x) => x + 1);
  }

  if (loading) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</div>
      )}

      <div className="flex justify-end">
        <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus className="mr-1.5 h-4 w-4" />
          {t('apiBuilder.endpoints.create')}
        </Button>
      </div>

      {formOpen && (
        <EndpointForm
          initial={editing}
          onSaved={handleSaved}
          onCancel={() => { setFormOpen(false); setEditing(null); }}
        />
      )}

      {endpoints.length === 0 && !formOpen ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('apiBuilder.endpoints.empty')}
        </div>
      ) : (
        <div className="space-y-2">
          {endpoints.map((ep) => (
            <Card key={ep.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{ep.name}</span>
                    <span className={cn(
                      'rounded px-1.5 py-0.5 text-[10px] font-semibold',
                      ep.enabled ? 'bg-green-900/40 text-green-300' : 'bg-muted text-muted-foreground',
                    )}>
                      {ep.enabled ? 'ON' : 'OFF'}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span><Code2 className="mr-0.5 inline h-3 w-3" />{ep.method} /{ep.pathSuffix}</span>
                    <span>{t('apiBuilder.endpoints.authType')}: {ep.authType}</span>
                    <span>{t('apiBuilder.endpoints.rateLimit')}: {ep.rateLimitPerMinute}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(ep)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(ep.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Endpoint form (structured query builder) ----

function EndpointForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial: ApiEndpoint | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  const parseTemplate = (json: string): QueryTemplate => {
    try { return JSON.parse(json); } catch { return { table: 'leads', operation: 'list', filter: {} }; }
  };

  const [name, setName] = useState(initial?.name ?? '');
  const [pathSuffix, setPathSuffix] = useState(initial?.pathSuffix ?? '');
  const [method, setMethod] = useState<'GET' | 'POST'>(initial?.method ?? 'GET');
  const [authType, setAuthType] = useState<'API_KEY' | 'JWT' | 'OAUTH'>(initial?.authType ?? 'API_KEY');
  const [rateLimit, setRateLimit] = useState(initial?.rateLimitPerMinute ?? 100);
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [template, setTemplate] = useState<QueryTemplate>(
    initial ? parseTemplate(initial.queryTemplateJson) : { table: 'leads', operation: 'list', filter: {} },
  );
  const [filterEntries, setFilterEntries] = useState<[string, string][]>(
    Object.entries(template.filter),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentColumns = FILTER_COLUMNS[template.table] ?? [];
  const currentSumCols = SUM_COLUMNS[template.table] ?? [];

  function buildTemplateJson(): string {
    const f: Record<string, string> = {};
    for (const [k, v] of filterEntries) {
      if (k && v) f[k] = v;
    }
    const tpl: QueryTemplate = {
      table: template.table,
      operation: template.operation,
      filter: f,
    };
    if (template.operation === 'sum') tpl.column = template.column;
    if (template.operation === 'list') tpl.limit = template.limit ?? 100;
    return JSON.stringify(tpl);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      name,
      pathSuffix,
      method,
      authType,
      queryTemplateJson: buildTemplateJson(),
      rateLimitPerMinute: rateLimit,
      enabled,
    };
    try {
      if (initial) {
        await apiClient.put(`/api-builder/endpoints/${initial.id}`, payload);
      } else {
        await apiClient.post('/api-builder/endpoints', payload);
      }
      onSaved();
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('apiBuilder.endpoints.error_save'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-5">
      <h3 className="mb-4 font-medium">
        {initial ? t('apiBuilder.endpoints.edit') : t('apiBuilder.endpoints.create')}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-xs font-medium">{t('apiBuilder.endpoints.name')}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">{t('apiBuilder.endpoints.pathSuffix')}</label>
            <Input value={pathSuffix} onChange={(e) => setPathSuffix(e.target.value)} placeholder="open-opps" required />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="mb-1 block text-xs font-medium">{t('apiBuilder.endpoints.method')}</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={method}
              onChange={(e) => setMethod(e.target.value as 'GET' | 'POST')}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">{t('apiBuilder.endpoints.authType')}</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={authType}
              onChange={(e) => setAuthType(e.target.value as 'API_KEY' | 'JWT' | 'OAUTH')}
            >
              <option value="API_KEY">API_KEY</option>
              <option value="JWT">JWT</option>
              <option value="OAUTH">OAUTH (stub)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">{t('apiBuilder.endpoints.rateLimit')}</label>
            <Input
              type="number"
              min={1}
              max={10000}
              value={rateLimit}
              onChange={(e) => setRateLimit(Number(e.target.value))}
            />
          </div>
        </div>

        {/* Query template builder */}
        <div className="rounded-md border border-border p-4">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('apiBuilder.endpoints.queryTemplate')}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium">{t('apiBuilder.endpoints.table')}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={template.table}
                onChange={(e) => {
                  setTemplate({ ...template, table: e.target.value, filter: {}, column: undefined });
                  setFilterEntries([]);
                }}
              >
                {ALLOWED_TABLES.map((tbl) => (
                  <option key={tbl} value={tbl}>{tbl}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium">{t('apiBuilder.endpoints.operation')}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={template.operation}
                onChange={(e) => setTemplate({ ...template, operation: e.target.value as QueryTemplate['operation'] })}
              >
                <option value="list">{t('apiBuilder.endpoints.operations.list')}</option>
                <option value="count">{t('apiBuilder.endpoints.operations.count')}</option>
                <option value="sum">{t('apiBuilder.endpoints.operations.sum')}</option>
              </select>
            </div>
          </div>

          {template.operation === 'sum' && currentSumCols.length > 0 && (
            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium">{t('apiBuilder.endpoints.sumColumn')}</label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={template.column ?? currentSumCols[0]}
                onChange={(e) => setTemplate({ ...template, column: e.target.value })}
              >
                {currentSumCols.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          )}

          {template.operation === 'list' && (
            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium">{t('apiBuilder.endpoints.limit')}</label>
              <Input
                type="number"
                min={1}
                max={500}
                value={template.limit ?? 100}
                onChange={(e) => setTemplate({ ...template, limit: Number(e.target.value) })}
                className="w-32"
              />
            </div>
          )}

          {/* Filters */}
          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium">{t('apiBuilder.endpoints.filter')}</label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setFilterEntries([...filterEntries, [currentColumns[0] ?? '', '']])}
              >
                <Plus className="mr-1 h-3 w-3" />
                {t('apiBuilder.endpoints.addFilter')}
              </Button>
            </div>
            {filterEntries.map((entry, i) => (
              <div key={i} className="mt-1 flex items-center gap-2">
                <select
                  className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                  value={entry[0]}
                  onChange={(e) => {
                    const updated = [...filterEntries];
                    updated[i] = [e.target.value, entry[1]];
                    setFilterEntries(updated);
                  }}
                >
                  {currentColumns.map((col) => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
                <Input
                  className="flex-1"
                  placeholder={t('apiBuilder.endpoints.filterValue')}
                  value={entry[1]}
                  onChange={(e) => {
                    const updated = [...filterEntries];
                    updated[i] = [entry[0], e.target.value];
                    setFilterEntries(updated);
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setFilterEntries(filterEntries.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="ep-enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          <label htmlFor="ep-enabled" className="text-sm">{t('apiBuilder.endpoints.enabled')}</label>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button type="submit" disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ---- API Keys tab ----

function ApiKeysTab() {
  const { t } = useTranslation();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newRawKey, setNewRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get<ApiKey[]>('/api-builder/keys')
      .then(({ data }) => setKeys(data))
      .catch(() => setKeys([]))
      .finally(() => setLoading(false));
  }, [refreshToken]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const { data } = await apiClient.post<{ id: string; name: string; rawKey: string }>(
        '/api-builder/keys',
        { name: newKeyName.trim() },
      );
      setNewRawKey(data.rawKey);
      setNewKeyName('');
      setRefreshToken((x) => x + 1);
    } catch {
      setError(t('apiBuilder.keys.error_save'));
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm(t('apiBuilder.keys.deleteConfirm'))) return;
    try {
      await apiClient.delete(`/api-builder/keys/${id}`);
      setRefreshToken((x) => x + 1);
    } catch {
      setError(t('apiBuilder.keys.error_delete'));
    }
  }

  function handleCopy() {
    if (!newRawKey) return;
    navigator.clipboard.writeText(newRawKey).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }

  if (loading) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('apiBuilder.keys.subtitle')}</p>

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</div>
      )}

      {/* New raw key alert */}
      {newRawKey && (
        <div className="rounded-md border border-amber-700 bg-amber-950/40 p-4">
          <p className="mb-2 text-sm font-medium text-amber-200">
            {t('apiBuilder.keys.newKeyWarning')}
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-card px-3 py-2 font-mono text-xs">{newRawKey}</code>
            <Button size="sm" variant="ghost" onClick={handleCopy}>
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      {/* Create form */}
      <Card className="p-4">
        <form onSubmit={handleCreate} className="flex gap-2">
          <Input
            placeholder={t('apiBuilder.keys.name')}
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            className="flex-1"
          />
          <Button type="submit" size="sm" disabled={creating || !newKeyName.trim()}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('apiBuilder.keys.create')}
          </Button>
        </form>
      </Card>

      {keys.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">{t('apiBuilder.keys.empty')}</div>
      ) : (
        <div className="space-y-2">
          {keys.map((k) => (
            <Card key={k.id} className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium">{k.name}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {t('apiBuilder.keys.createdAt')}: {new Date(k.createdAt).toLocaleDateString()}
                  {' · '}
                  {t('apiBuilder.keys.lastUsed')}: {k.lastUsedAt
                    ? new Date(k.lastUsedAt).toLocaleDateString()
                    : t('apiBuilder.keys.never')}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(k.id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
